import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  MapPin, 
  Calendar, 
  ExternalLink, 
  Award,
  Users,
  TrendingUp,
  Clock,
  Building
} from 'lucide-react';
import { experiences } from '../data/mock';

const Experience = () => {
  const totalExperience = experiences.reduce((total, exp) => {
    const duration = exp.duration.split(' - ')[1] === 'Present' ? 
      new Date().getFullYear() - parseInt(exp.duration.split(' - ')[0]) :
      parseInt(exp.duration.split(' - ')[1]) - parseInt(exp.duration.split(' - ')[0]);
    return total + duration;
  }, 0);

  const stats = [
    { label: 'Years Experience', value: `5+`, icon: Clock },
    { label: 'Companies', value: "5", icon: Building },
    { label: 'Projects Delivered', value: '50+', icon: TrendingUp },
    { label: 'Team Members Led', value: '15+', icon: Users },
  ];

  return (
    <div className="min-h-screen py-12">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-4xl lg:text-5xl font-bold mb-4">
            Professional{' '}
            <span className="bg-gradient-to-r from-purple-600 to-black bg-clip-text text-transparent">
              Experience
            </span>
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            My journey through different roles and companies, showcasing growth in technical expertise and leadership skills.
          </p>
        </div>

        {/* Stats Section */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {stats.map((stat, index) => (
            <Card key={index} className="text-center p-6 hover:shadow-lg transition-shadow">
              <CardContent className="p-0 space-y-4">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-purple-100 dark:bg-purple-900/20">
                  <stat.icon className="h-6 w-6 text-purple-600" />
                </div>
                <div>
                  <div className="text-3xl font-bold text-purple-600">{stat.value}</div>
                  <div className="text-sm text-muted-foreground">{stat.label}</div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Experience Timeline */}
        <div className="relative">
          {/* Timeline Line */}
          <div className="absolute left-8 top-0 bottom-0 w-px bg-gradient-to-b from-purple-600 via-purple-400 to-purple-200 hidden lg:block"></div>
          
          <div className="space-y-12">
            {experiences.map((experience, index) => (
              <div key={experience.id} className="relative">
                {/* Timeline Dot */}
                <div className="absolute left-6 w-4 h-4 bg-purple-600 rounded-full border-4 border-background z-10 hidden lg:block"></div>
                
                {/* Experience Card */}
                <div className="lg:ml-20">
                  <Card className="hover:shadow-xl transition-all duration-300 overflow-hidden">
                    <CardContent className="p-0">
                      <div className="grid lg:grid-cols-3 gap-0">
                        {/* Main Content */}
                        <div className="lg:col-span-2 p-8">
                          <div className="space-y-6">
                            {/* Header */}
                            <div className="space-y-3">
                              <div className="flex flex-wrap items-center gap-3">
                                <Badge className="bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-300">
                                  {experience.type}
                                </Badge>
                                <div className="flex items-center text-sm text-muted-foreground">
                                  <Calendar className="w-4 h-4 mr-1" />
                                  {experience.duration}
                                </div>
                                <div className="flex items-center text-sm text-muted-foreground">
                                  <MapPin className="w-4 h-4 mr-1" />
                                  {experience.location}
                                </div>
                              </div>
                              
                              <div>
                                <h2 className="text-2xl font-bold text-purple-600 mb-1">
                                  {experience.role}
                                </h2>
                                <h3 className="text-xl font-semibold text-foreground">
                                  {experience.company}
                                </h3>
                              </div>
                            </div>

                            {/* Description */}
                            <p className="text-muted-foreground leading-relaxed">
                              {experience.description}
                            </p>

                            {/* Key Achievements */}
                            <div>
                              <h4 className="font-semibold mb-3 flex items-center">
                                <Award className="w-4 h-4 mr-2 text-purple-600" />
                                Key Achievements
                              </h4>
                              <ul className="space-y-2">
                                {experience.achievements.map((achievement, achievementIndex) => (
                                  <li key={achievementIndex} className="flex items-start">
                                    <div className="w-2 h-2 bg-purple-600 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                                    <span className="text-muted-foreground">{achievement}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </div>
                        </div>

                        {/* Technologies Sidebar */}
                        <div className="bg-muted/30 p-8 border-l border-border">
                          <div className="space-y-6">
                            <div>
                              <h4 className="font-semibold mb-4">Technologies Used</h4>
                              <div className="flex flex-wrap gap-2">
                                {experience.technologies.map((tech, techIndex) => (
                                  <Badge key={techIndex} variant="outline" className="text-xs">
                                    {tech}
                                  </Badge>
                                ))}
                              </div>
                            </div>

                            {/* Experience Duration Indicator */}
                            <div>
                              <h4 className="font-semibold mb-3">Duration</h4>
                              <div className="flex items-center gap-2">
                                <div className="w-full bg-muted rounded-full h-2">
                                  <div 
                                    className="bg-gradient-to-r from-purple-600 to-purple-700 h-2 rounded-full"
                                    style={{ 
                                      width: experience.duration.includes('Present') ? '100%' : 
                                        `${Math.min(100, (parseInt(experience.duration.split(' - ')[1]) - parseInt(experience.duration.split(' - ')[0]) + 1) * 33)}%`
                                    }}
                                  ></div>
                                </div>
                                <span className="text-sm text-muted-foreground whitespace-nowrap">
                                  {experience.duration.includes('Present') ? 'Current' : 
                                    `${parseInt(experience.duration.split(' - ')[1]) - parseInt(experience.duration.split(' - ')[0]) + 1}yr`}
                                </span>
                              </div>
                            </div>

                            {/* Growth Indicator */}
                            <div className="bg-purple-50 dark:bg-purple-950/20 rounded-lg p-4">
                              <div className="flex items-center gap-2 text-sm">
                                <TrendingUp className="w-4 h-4 text-purple-600" />
                                <span className="font-medium text-purple-700 dark:text-purple-300">
                                  Professional Growth
                                </span>
                              </div>
                              <p className="text-xs text-purple-600 dark:text-purple-400 mt-1">
                                {index === 0 ? 'Current Role' : 
                                 index === 1 ? 'Career Advancement' : 
                                 'Foundation Building'}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Skills Evolution Section */}
        <div className="mt-20 py-16 bg-gradient-to-r from-purple-50 to-black/5 dark:from-purple-950/20 dark:to-black/40 rounded-3xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Skills Evolution</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              How my technical expertise has grown and diversified throughout my career journey
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="p-6 text-center">
              <CardContent className="p-0 space-y-4">
                <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/20 rounded-full flex items-center justify-center mx-auto">
                  <span className="text-lg font-bold text-blue-600">2021</span>
                </div>
                <div>
                  <h3 className="font-semibold mb-2">Foundation Phase</h3>
                  <p className="text-sm text-muted-foreground mb-3">
                    Started with full-stack development fundamentals
                  </p>
                  <div className="flex flex-wrap gap-1 justify-center">
                    <Badge variant="outline" className="text-xs">React</Badge>
                    <Badge variant="outline" className="text-xs">Express</Badge>
                    <Badge variant="outline" className="text-xs">PostgreSQL</Badge>
                    <Badge variant="outline" className="text-xs">Docker</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="p-6 text-center">
              <CardContent className="p-0 space-y-4">
                <div className="w-16 h-16 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center mx-auto">
                  <span className="text-lg font-bold text-green-600">2022</span>
                </div>
                <div>
                  <h3 className="font-semibold mb-2">Specialization Phase</h3>
                  <p className="text-sm text-muted-foreground mb-3">
                    Deep dive into data engineering and cloud technologies
                  </p>
                  <div className="flex flex-wrap gap-1 justify-center">
                    <Badge variant="outline" className="text-xs">AWS</Badge>
                    <Badge variant="outline" className="text-xs">PySpark</Badge>
                    <Badge variant="outline" className="text-xs">Kafka</Badge>
                    <Badge variant="outline" className="text-xs">Elasticsearch</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="p-6 text-center">
              <CardContent className="p-0 space-y-4">
                <div className="w-16 h-16 bg-purple-100 dark:bg-purple-900/20 rounded-full flex items-center justify-center mx-auto">
                  <span className="text-lg font-bold text-purple-600">2023+</span>
                </div>
                <div>
                  <h3 className="font-semibold mb-2">Leadership Phase</h3>
                  <p className="text-sm text-muted-foreground mb-3">
                    Combining technical skills with business solutions
                  </p>
                  <div className="flex flex-wrap gap-1 justify-center">
                    <Badge variant="outline" className="text-xs">System Design</Badge>
                    <Badge variant="outline" className="text-xs">Team Lead</Badge>
                    <Badge variant="outline" className="text-xs">Fintech</Badge>
                    <Badge variant="outline" className="text-xs">Mentoring</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Current Focus */}
        <div className="mt-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Current Learning Path</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Technologies and skills I'm actively developing to stay ahead in the rapidly evolving tech landscape
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <Card className="p-8 hover:shadow-lg transition-shadow">
              <CardContent className="p-0">
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-orange-100 dark:bg-orange-900/20 rounded-lg flex items-center justify-center">
                      <Building className="w-6 h-6 text-orange-600" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold">Advanced Backend Development</h3>
                      <p className="text-sm text-muted-foreground">Enterprise-grade architecture patterns</p>
                    </div>
                  </div>
                  <p className="text-muted-foreground">
                    Exploring microservices patterns, distributed systems, and advanced API design principles 
                    to build more scalable and maintainable backend solutions.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <Badge variant="secondary" className="text-xs">Microservices</Badge>
                    <Badge variant="secondary" className="text-xs">GraphQL</Badge>
                    <Badge variant="secondary" className="text-xs">Event Sourcing</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="p-8 hover:shadow-lg transition-shadow">
              <CardContent className="p-0">
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-red-100 dark:bg-red-900/20 rounded-lg flex items-center justify-center">
                      <Award className="w-6 h-6 text-red-600" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold">NestJS Framework</h3>
                      <p className="text-sm text-muted-foreground">Modern Node.js framework mastery</p>
                    </div>
                  </div>
                  <p className="text-muted-foreground">
                    Diving deep into NestJS ecosystem including decorators, guards, interceptors, 
                    and building enterprise-ready applications with TypeScript.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <Badge variant="secondary" className="text-xs">NestJS</Badge>
                    <Badge variant="secondary" className="text-xs">TypeScript</Badge>
                    <Badge variant="secondary" className="text-xs">Decorators</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* CTA Section */}
        <div className="mt-20 text-center">
          <h2 className="text-3xl font-bold mb-4">Let's Work Together</h2>
          <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
            With my diverse experience across different technologies and domains, I can help bring your ideas to life.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" asChild className="bg-purple-600 hover:bg-purple-700">
              <Link to="/contact">
                Hire Me
                <ExternalLink className="ml-2 h-4 w-4" />
              </Link>
            </Button>
            <Button size="lg" variant="outline" asChild>
              <Link to="/portfolio">
                View My Work
                <Award className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Experience;